import { isDebug } from "@/constants";
import type {
  ParticleSystemColorGradient,
  ParticleSystemGradient,
} from "@/types";
import {
  DataTexture,
  FloatType,
  NearestFilter,
  RedFormat,
  RGBAFormat,
  type Vector4Tuple,
} from "three";
import { randomColorInRange } from "./randomColorInRange";

export const buildColorGradientTexture = (
  gradient: ParticleSystemColorGradient,
  capacity: number,
) => {
  const stopsPerParticle = gradient.length;
  const stops = new Float32Array(stopsPerParticle);

  const colors = new Float32Array(capacity * stopsPerParticle * 4);
  for (let i = 0; i < capacity; i++) {
    gradient.forEach(({ color1, color2 }, index) => {
      let color: Vector4Tuple;
      if (color2) {
        color = randomColorInRange(color1, color2);
      } else {
        color = color1;
      }

      const colorI = i * stopsPerParticle * 4 + index * 4;
      const [r, g, b, a] = color;
      colors[colorI] = r;
      colors[colorI + 1] = g;
      colors[colorI + 2] = b;
      colors[colorI + 3] = a;
    });
  }
  gradient.forEach(({ gradient }, index) => {
    if (isDebug) {
      console.log("stop", gradient, "index", index);
    }

    stops[index] = gradient;
  });

  const width = 512; // mostly arbitrary. could make dynamic based on size and pass in as uniform
  const colorHeight = colors.length / width / 4;

  const colorTexture = new DataTexture(
    colors,
    width,
    colorHeight,
    RGBAFormat,
    FloatType,
    undefined,
    undefined,
    undefined,
    NearestFilter,
    NearestFilter,
  );
  colorTexture.needsUpdate = true;

  const stopTexture = new DataTexture(
    stops,
    stopsPerParticle,
    1,
    RedFormat,
    FloatType,
    undefined,
    undefined,
    undefined,
    NearestFilter,
    NearestFilter,
  );
  stopTexture.needsUpdate = true;

  return [colorTexture, stopTexture];
};

export const buildSizeGradientTexture = (
  gradient: ParticleSystemGradient,
  capacity: number,
) => {
  // Return null textures if gradient is empty
  if (!(gradient?.length > 0)) {
    return [null, null] as const;
  }

  const stopsPerParticle = gradient.length;
  const stops = new Float32Array(stopsPerParticle);

  const colors = new Float32Array(capacity * stopsPerParticle);
  for (let i = 0; i < capacity; i++) {
    gradient.forEach(({ factor1 }, index) => {
      const sizeI = i * stopsPerParticle + index;
      colors[sizeI] = factor1;
    });
  }
  gradient.forEach(({ gradient }, index) => {
    stops[index] = gradient;
  });

  const width = 512; // mostly arbitrary. could make dynamic based on size and pass in as uniform
  const colorHeight = colors.length / width;

  const colorTexture = new DataTexture(
    colors,
    width,
    colorHeight,
    RedFormat,
    FloatType,
    undefined,
    undefined,
    undefined,
    NearestFilter,
    NearestFilter,
  );
  colorTexture.needsUpdate = true;

  const stopTexture = new DataTexture(
    stops,
    stopsPerParticle,
    1,
    RedFormat,
    FloatType,
    undefined,
    undefined,
    undefined,
    NearestFilter,
    NearestFilter,
  );
  stopTexture.needsUpdate = true;

  return [colorTexture, stopTexture];
};
